import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import UpgradeGate from '../components/UpgradeGate';
import { toast } from 'sonner';
import { Users, UserCheck, UserMinus, UserX, Ghost, TrendingUp, Eye, Bell, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = { active: '#3E6641', at_risk: '#D4A373', lapsed: '#E2725B', lost: '#9B2C2C', never: '#B0B5A8' };

export default function RetentionPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('all');
  const [activeTab, setActiveTab] = useState('at_risk');

  const fetchLocations = useCallback(async () => {
    try {
      const { data: locs } = await axios.get(`${API}/locations`, { withCredentials: true });
      setLocations(Array.isArray(locs) ? locs : []);
    } catch {}
  }, []);

  const fetchRetention = useCallback(async () => {
    try {
      const params = locationId !== 'all' ? `?location_id=${locationId}` : '';
      const { data: d } = await axios.get(`${API}/retention/dashboard${params}`, { withCredentials: true });
      setData(d);
    } catch { toast.error('Failed to load retention data'); }
  }, [locationId]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);
  useEffect(() => { fetchRetention(); }, [fetchRetention]);

  if (!data) return <div className="p-8 text-center text-[#5C6656]">Loading retention data...</div>;

  const { stats, segments } = data;

  const pieData = [
    { name: 'Active (0-30d)', value: stats.active, color: COLORS.active },
    { name: 'At Risk (31-60d)', value: stats.at_risk, color: COLORS.at_risk },
    { name: 'Lapsed (61-90d)', value: stats.lapsed, color: COLORS.lapsed },
    { name: 'Lost (90d+)', value: stats.lost, color: COLORS.lost },
  ].filter(d => d.value > 0);

  const tabs = [
    { key: 'at_risk', label: 'At Risk', count: stats.at_risk, color: 'text-[#D4A373]', bg: 'bg-[#FDF3E7]' },
    { key: 'lapsed', label: 'Lapsed', count: stats.lapsed, color: 'text-[#E2725B]', bg: 'bg-[#FDE8E4]' },
    { key: 'lost', label: 'Lost', count: stats.lost, color: 'text-[#9B2C2C]', bg: 'bg-[#FDE2E2]' },
    { key: 'active', label: 'Active', count: stats.active, color: 'text-[#3E6641]', bg: 'bg-[#E9EDDF]' },
    { key: 'never_visited', label: 'Never Visited', count: stats.never_visited, color: 'text-[#5C6656]', bg: 'bg-[#F5F5F3]' },
  ];

  const currentClients = segments[activeTab] || [];

  return (
    <UpgradeGate feature="retention_dashboard" featureName="Client Retention">
    <div className="fade-in space-y-6" data-testid="retention-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-medium text-[#1A2421] tracking-tight" style={{ fontFamily: 'Outfit' }}>{t('retention.title')}</h1>
          <p className="text-sm text-[#5C6656] mt-1">Track client engagement and re-activate lapsed clients</p>
        </div>
        {locations.length > 1 && (
          <Select value={locationId} onValueChange={setLocationId}>
            <SelectTrigger className="w-[200px] border-[#E5E7E1]" data-testid="retention-location-filter">
              <SelectValue placeholder="All Locations" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Active', sublabel: '0-30 days', value: stats.active, icon: UserCheck, iconColor: 'text-[#3E6641]', bgIcon: 'bg-[#E9EDDF]' },
          { label: 'At Risk', sublabel: '31-60 days', value: stats.at_risk, icon: UserMinus, iconColor: 'text-[#D4A373]', bgIcon: 'bg-[#FDF3E7]' },
          { label: 'Lapsed', sublabel: '61-90 days', value: stats.lapsed, icon: UserX, iconColor: 'text-[#E2725B]', bgIcon: 'bg-[#FDE8E4]' },
          { label: 'Lost', sublabel: '90+ days', value: stats.lost, icon: Ghost, iconColor: 'text-[#9B2C2C]', bgIcon: 'bg-[#FDE2E2]' },
          { label: 'Retention Rate', sublabel: 'active / total', value: `${stats.retention_rate}%`, icon: TrendingUp, iconColor: stats.retention_rate >= 50 ? 'text-[#3E6641]' : 'text-[#E2725B]', bgIcon: stats.retention_rate >= 50 ? 'bg-[#E9EDDF]' : 'bg-[#FDE8E4]' },
        ].map((card, i) => {
          const Icon = card.icon;
          return (
            <Card key={i} className="border-[#E5E7E1] shadow-sm" data-testid={`retention-stat-${card.label.toLowerCase().replace(/\s/g, '-')}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold tracking-wider uppercase text-[#5C6656]">{card.label}</p>
                  <div className={`w-8 h-8 rounded-lg ${card.bgIcon} flex items-center justify-center`}>
                    <Icon size={16} className={card.iconColor} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-[#1A2421]">{card.value}</p>
                <p className="text-[10px] text-[#5C6656] mt-0.5">{card.sublabel}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Pie Chart */}
        <Card className="border-[#E5E7E1] shadow-sm" data-testid="retention-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-[#1A2421]" style={{ fontFamily: 'Outfit' }}>Client Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value">
                    {pieData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                  </Pie>
                  <Tooltip formatter={(v, name) => [v, name]} contentStyle={{ borderRadius: 8, border: '1px solid #E5E7E1', fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[220px] text-sm text-[#5C6656]">No data yet</div>
            )}
            <div className="space-y-2 mt-2">
              {pieData.map(d => (
                <div key={d.name} className="flex items-center gap-2 text-xs">
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: d.color }} />
                  <span className="text-[#5C6656] flex-1">{d.name}</span>
                  <span className="font-semibold text-[#1A2421]">{d.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Client List */}
        <div className="lg:col-span-2">
          <Card className="border-[#E5E7E1] shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2 flex-wrap">
                {tabs.map(tab => (
                  <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${activeTab === tab.key ? `${tab.bg} ${tab.color}` : 'text-[#5C6656] hover:bg-[#F9F8F6]'}`}
                    data-testid={`retention-tab-${tab.key}`}>
                    {tab.label} ({tab.count})
                  </button>
                ))}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[500px] overflow-y-auto">
                {currentClients.length === 0 ? (
                  <div className="py-12 text-center text-sm text-[#5C6656]">No clients in this segment</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white border-b border-[#E5E7E1]">
                      <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-[#5C6656]">
                        <th className="px-5 py-3">Client</th>
                        <th className="px-3 py-3">Last Visit</th>
                        <th className="px-3 py-3">Visits</th>
                        <th className="px-3 py-3">Spend</th>
                        <th className="px-3 py-3">Top Service</th>
                        <th className="px-3 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentClients.map(c => (
                        <tr key={c.client_id} className="border-b border-[#E5E7E1] last:border-0 hover:bg-[#F9F8F6] transition-colors" data-testid={`retention-client-${c.client_id}`}>
                          <td className="px-5 py-3">
                            <p className="font-medium text-[#1A2421]">{c.name}</p>
                            <p className="text-[11px] text-[#5C6656]">{c.email || c.phone}</p>
                          </td>
                          <td className="px-3 py-3">
                            {c.last_visit ? (
                              <div className="flex items-center gap-1">
                                <span className="text-[#1A2421]">{c.last_visit}</span>
                                <Badge className={`text-[9px] ml-1 ${c.days_since <= 30 ? 'bg-[#E9EDDF] text-[#3E6641]' : c.days_since <= 60 ? 'bg-[#FDF3E7] text-[#D4A373]' : c.days_since <= 90 ? 'bg-[#FDE8E4] text-[#E2725B]' : 'bg-[#FDE2E2] text-[#9B2C2C]'}`}>
                                  {c.days_since}d ago
                                </Badge>
                              </div>
                            ) : <span className="text-[#B0B5A8]">Never</span>}
                          </td>
                          <td className="px-3 py-3 text-[#1A2421]">{c.total_visits}</td>
                          <td className="px-3 py-3 font-medium text-[#1A2421]">{'\u00A3'}{(c.total_spend || 0).toFixed(2)}</td>
                          <td className="px-3 py-3 text-[#5C6656] text-xs truncate max-w-[140px]">{c.favourite_service || '-'}</td>
                          <td className="px-3 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-[#556B2F] hover:bg-[#E9EDDF]" onClick={() => navigate(`/clients/${c.client_id}`)} data-testid={`view-profile-${c.client_id}`}>
                                <Eye size={13} className="mr-1" /> View
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-[#D4A373] hover:bg-[#FDF3E7]" onClick={() => toast.info('Reminder feature coming soon (awaiting email/SMS integration)')} data-testid={`send-reminder-${c.client_id}`}>
                                <Bell size={13} className="mr-1" /> Remind
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
    </UpgradeGate>
  );
}
